import { ScreenManager } from "../../managers/ScreenManager";
import { CardData, OptionsItemLiveData } from "../../types/types";
import { Card, CardRank, CardSuit } from "../card/Card";
import { OptionsDataManager } from "../managers/OptionsDataManager";
import { AppDebug } from "../debug/AppDebug";
import { AppLocalization, LanguageId } from "../localization/AppLocalization";
import { AppSettings, GamePlayDirection } from "../managers/AppSettings";
import { CardsManager } from "../managers/CardsManager";
import { LayoutDataManager, OptionsItemKeys } from "../managers/LayoutDataManager";
import { Player } from "../player/Player";
import { Players } from "../player/Players";
import { TableScreen, VariationId } from "../screen/TableScreen";
import { TableStateMachine, TrumpSuit } from "../state/TableStateMachine";
import { ButtonBubble } from "../ui/ButtonBubble";
import { CardSuitToTrumpSuit } from "../utils/CardSuitToTrumpSuit";
import { AppTimings } from "../data/AppConstants";

/**
 * NOTE: order tied to localization strings
 */
export enum DeclarationType {
    /**
     * Announcement Belote - right after placing King or Queen (first of the two / any order) on table
     * Announcement Rebelote - right after placing King or Queen (second of the two / any order) on table
     * - these two announcements are merged into a single declaration
     * - BELOTE/REBELOTE cards MUST BE of TRUMP suit.
     * Type: Minor Announcement
     */
    BELOTE_REBELOTE,

    /**
     * 3 cards of same suit - 20pts
     * Type: Major Announcement
     */
    TIERCE,

    /**
     * 4 cards of same suit - 50pts
     * Type: Major Announcement
     */
    QUARTE,

    /**
     * 5 cards of same suit - 100pts
     * Type: Major Announcement
     */
    QUINT,

    /**
     * 4 same ranked cards, diff suits
     * - of Jacks - 200pts
     * - of Nines - 150pts
     * - of Aces || Tens || Kings || Queens - 100pts 
     * NOTE: ignore SEVENS and EIGHTS, as they do not form a SQUARE declaration
     * Type: Major Announcement
     */
    SQUARE,
}

/**
 * Note a player can have multiple dfeclarations of the same type, for ex
 * different @see DeclarationType.BELOTE_REBELOTE declarations, but with different cards
 */
export type PlayerDeclaration = {
    playerIndex: number,
    declarationType: DeclarationType,
    declarationValue: number,
    declarationCards: DeclarationCards,
}

export type DeclarationCards = {
    cards: CardData[],
    played: boolean[],
}

/**
 * Rules of announcements:
 * Eryodsoft Coinche:
 * - After the first trick, when the first card of the second trick is played,
 * we move on to the “resolution” or “reveal” of the announcements. The one who has 
 * the best announcement scores the equivalent number of points straightaway.
 *  - Eroydsoft only displays 1 announcement at that point, the highest one, 
 *  and points are automatically added into brackets as (bonus points) to team 
 *  with highest announcement.
 * @see 'belote-eryod-random_game-1500score.mkv' @ 03:00 || 07:10 for QUARTE
 * @see 'belote-eryod-random_game-1500score.mkv' @ 06:00 for Belote/Rebelote Deal
 * NOTE: 
 * In coinche, the points are all rounded to the nearest ten.
 * Example: 95 points get rounded to 100, whereas 94 get rounded to 90.
 * Does this mean: Capot: 252 in Belote 250 in Coinche???
 * 
 * Coinche Rules:
 * https://web.archive.org/web/20220129124440/https://www.funbelote.com/en/rules-belote/coinche/
 */
export class DeclarationsManager {
    public table: TableScreen;
    private waitTween: Phaser.Tweens.Tween;

    public Init(table: TableScreen): void {
        this.table = table;
    }

    /**
     * 
     * @param declarations 
     * @param onComplete When all are completed playing, this triggers.
     */
    public InjectDeclarationAnimations(
        declarations: PlayerDeclaration[],
        onComplete: Function
    ): void {
        // DEBUG player declaration animation and spacing
        if (AppDebug.GetDebugMajorDeclarations()) {
            const player: Player = this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX);
            const declCards: Card[] = player.GetCards().slice(0,4);
            declarations = [
                {
                    playerIndex: Players.PLAYER_INDEX,
                    declarationType: DeclarationType.QUARTE, // 4-sequence, same suit
                    declarationValue: 100,
                    declarationCards: {
                        cards: declCards,
                        played: declCards.map(card => false),
                    }
                }
            ];
        }

        const hasMajorDeclarations = declarations.length > 0;
        if (hasMajorDeclarations) {
            this.InjectDeclarationAnimation(declarations, onComplete);
        }
        else {
            onComplete();
        }
    }

    /**
     * Serialized scheduler to play dec animations one by one.
     * @param restDeclarations Remaining declarations scheduled to be played (first is played instantly, rest is passed to next @see InjectDeclarationAnimation to be played after)
     * @param onComplete Runs when all declarations are finished playing (restDeclarations==[])
     */
    private InjectDeclarationAnimation(
        restDeclarations: PlayerDeclaration[],
        onComplete: Function
    ): void {
        // console.log("InjectDeclarationAnimation; declaration:", declaration);

        const declaration = restDeclarations.pop();
        const player: Player = this.table.players.GetPlayerByIndex(declaration.playerIndex);
        const playerCards: Card[] = player.GetCards();
        let trumpSuit: TrumpSuit = this.table.GetStateMachine().GetData().trumpSuit;
        const rankLeftToRight: boolean = OptionsDataManager.IsRankLeftToRight();
        const stackLeftToRight: boolean = OptionsDataManager.IsStackLeftToRight();

        const matchingPlayerCards: Card[] = CardsManager.GetCardsByCardData(declaration.declarationCards.cards, playerCards);
        const matchingPoolCards: Card[] = CardsManager.GetCardsByCardData(declaration.declarationCards.cards, CardsManager.GetCards());

        let bubbleText = "";
        let sortedCards: Card[] = [...matchingPlayerCards, ...matchingPoolCards];
        switch (declaration.declarationType) {
            case DeclarationType.SQUARE:
                bubbleText = AppLocalization.GetVariationData().table.declarations.square;
                break;
            case DeclarationType.TIERCE:
                bubbleText = AppLocalization.GetVariationData().table.declarations.tierce;
                break;
            case DeclarationType.QUARTE:
                bubbleText = AppLocalization.GetVariationData().table.declarations.quarte;
                break;
            case DeclarationType.QUINT:
                bubbleText = AppLocalization.GetVariationData().table.declarations.quint;
                sortedCards.sort((cardA, cardB) => cardA.rank - cardB.rank);
                break;
        }

        // extract default card props to be recovered after declaration animation
        let sortedCardsProps = [];
        sortedCards.forEach((card, cardIndex) => {
            // const matchingPlayerCardsCardIndex = matchingPlayerCards.indexOf(card);
            sortedCardsProps[cardIndex] = [];
            // sortedCardsProps[cardIndex].containerCardIndex = matchingPlayerCardsCardIndex;
            // sortedCardsProps[cardIndex].cardIndex = cardIndex;
            // sortedCardsProps[cardIndex].x = card.x;
            // sortedCardsProps[cardIndex].y = card.x;
            sortedCardsProps[cardIndex].frontFacing = card.IsFrontFacing();
            // sortedCardsProps[cardIndex].parentScale = card.parentContainer.scale;
            // sortedCardsProps[cardIndex].angle = card.angle;
            // sortedCardsProps[cardIndex].frontFacing = card.IsFrontFacing();
        })

        // player bubble with declaration type
        // const bubbleText: string = DeclarationType[declaration.declarationType].toString();
        
        // console.log(LanguageId[AppLocalization.GetCurLanguageId()]);
        // console.log(AppLocalization.GetVariationData());
        
        this.table.players.GetPlayerByIndex(declaration.playerIndex).Say(bubbleText);

        let majorDeclarationShowDuration = OptionsDataManager.GetTiming(AppTimings.majorDeclarationShowDuration);
        let majorDeclarationVisibleDuration = OptionsDataManager.GetTiming(AppTimings.majorDeclarationVisibleDuration);
        let majorDeclarationHideDuration = OptionsDataManager.GetTiming(AppTimings.majorDeclarationHideDuration);
        // DEBUG
        // majorDeclarationShowDuration = 1000;
        // majorDeclarationVisibleDuration = 3000;
        // majorDeclarationHideDuration = 1000;

        this.table.scene.tweens.add({
            targets: sortedCards,
            duration: majorDeclarationHideDuration,
            alpha: 0,
            onStart: () => {
                //     const flipCard = !card.IsFrontFacing();
            },
            // done fading out cards
            onComplete: () => {
                // const pickedPlayerCards: Card[] = 
                player.PickCards(matchingPlayerCards);

                // const pickedPoolCards: Card[] = 
                // CardsManager.PickCards2(matchingPoolCards); // frontFacing?
                // TODO: test overload
                CardsManager.PickCards(matchingPoolCards); // frontFacing?

                // make cards front facing
                // sort by depth (left to right)
                // position cards within container (container is always screen-centered)
                // card spacing already affected by game quality/AR/etc - on level of container
                const cardSpacing = LayoutDataManager.GetSharedData().table.majorDeclarations.cardSpacing;
                sortedCards.forEach((card, cardIndex) => {
                    // set cards front facing
                    card.SetFrontFacing(true);
                    card.angle = 0;

                    // console.log(card.parentContainer) // null
                    // drop into declarationsContainer
                    this.table.majorDeclarationContainer.add(card);

                    // position cards within container (center around (0,0))
                    card.setPosition(
                        (cardIndex - ((sortedCards.length - 1) / 2)) * cardSpacing,
                        card.y = 0
                    );
                });

                const hideMajorDeclaration: Function = () => {
                    // fade out declaration
                    this.table.scene.tweens.add({
                        targets: sortedCards,
                        duration: majorDeclarationHideDuration,
                        alpha: 0,
                        // when done fading out declaration
                        onComplete: () => {
                            // reset to default card props
                            sortedCards.forEach((card, cardIndex) => {
                                // card.x = sortedCardsProps[cardIndex].x;
                                // card.y = sortedCardsProps[cardIndex].y;
                                card.SetFrontFacing(sortedCardsProps[cardIndex].frontFacing);
                            });

                            // remove all cards from container
                            this.table.majorDeclarationContainer.removeAll();

                            // drop cards into player's hand and pool
                            player.DropCards(
                                matchingPlayerCards,
                                true,
                                trumpSuit,
                                rankLeftToRight,
                                stackLeftToRight,
                                false,
                            );
                            // disable and clear effect
                            CardsManager.DropCards(matchingPoolCards);

                            let cardFlipNextTaskDelay = OptionsDataManager.GetTiming(AppTimings.cardFlipNextTaskDelay);
                            this.waitTween?.destroy();
                            this.waitTween = this.table.scene.tweens.addCounter({
                                duration: cardFlipNextTaskDelay,
                                onComplete: () => {
                                    this.table.scene.tweens.add({
                                        targets: sortedCards,
                                        duration: majorDeclarationShowDuration,
                                        alpha: 1,
                                        onComplete: () => {
                                            restDeclarations.length ? 
                                                this.InjectDeclarationAnimation(restDeclarations, onComplete) :
                                                onComplete();
                                        },
                                    });
                                },
                            });
                        }
                    });
                };

                // fade in declaration
                this.table.scene.tweens.add({
                    targets: sortedCards,
                    duration: majorDeclarationShowDuration,
                    alpha: 1,
                    // when done fading in declaration
                    onComplete: () => {
                        // should declaration be autocollected?
                        const autoCollectDeclarations: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.autoCollectDeclarations, this.table.variationId);

                        // yes
                        if (autoCollectDeclarations && autoCollectDeclarations.value) {
                            // keep a bit on screen
                            this.table.scene.tweens.addCounter({
                                duration: majorDeclarationVisibleDuration,
                                // then collect
                                onComplete: () => {
                                    hideMajorDeclaration();
                                },
                            });
                        }
                        // no
                        else {
                            // give user a button to click to collect
                            const accepterBubble: ButtonBubble = this.table.players.GetPlayerByIndex(Players.PLAYER_INDEX).acceptorBubble;
                            accepterBubble.SetButtonLabel(AppLocalization.GetSharedData().table.acceptPopup.btnPrimary.itemLabel);
                            accepterBubble.UpdateView();
                            accepterBubble.SetData({ onComplete: hideMajorDeclaration });
                            accepterBubble.Show();
                        }
                    },
                });
            },
        })
    }

    /**
     * For AI/player hints considering declarations in Bid
     */
    public GetPlayerDeclarationsValue(playerIndex: number, trumpSuit: TrumpSuit): number {
        const players = this.table.players;
        const playWithDeclarations = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.playWithDeclarations, this.table.variationId);
        const isPlayWithDeclarations = playWithDeclarations && (playWithDeclarations.value as boolean);
        const player = players.GetPlayerByIndex(playerIndex);
        const playerCards = player.GetCards();
        let totalValue: number = 0;
    
        // player major declarations
        if (isPlayWithDeclarations) {
            const playerDeclarationsSquare = this.GetPlayerDeclarationsSquares(playerIndex, playerCards, trumpSuit);
            totalValue += playerDeclarationsSquare.reduce((total, d)=>{
                total += d.declarationValue;
                return total;
            }, 0);
            const playerDeclarationsSequence = this.GetPlayerDeclarationsSequences(playerIndex, playerCards, trumpSuit);
            totalValue += playerDeclarationsSequence.reduce((total, d)=>{
                total += d.declarationValue;
                return total;
            }, 0);
        }
        // player belotes
        const playerDeclarationsBelote: PlayerDeclaration[] = this.GetPlayerDeclarationsBelote(playerIndex, playerCards, trumpSuit);
        totalValue += playerDeclarationsBelote.reduce((total, d)=>{
            total += d.declarationValue;
            return total;
        }, 0);
        
        return totalValue;
    }

    /**
     * Resolves players' hands for declarations and returns them as data model to be used by table stateMachine
     * - only resolve highest declaration and assign it to playerIndex of player that has it on hand
     * - DeclarationsManager is also used to animate said declaration after first trick is played during PLAY PHASE
     * - BELOTE/REBELOTE is assigned separately and always to each player hand that has it, in case of 
     *   ALL_TRUMPS, there can be total of 4 belote/rebelotes assigned (to multiple or even just 1 hand [unlikely])
     * - also counts declaration values types and caches them in returned data model
     * 
     * This is how winning declaration is counted:
        A square is higher than a 5 card sequence. Between equally long sequences, the one containing the higher ranked cards wins. If there are two sequences of equal rank, and one is trumps, the trump sequence wins. If none is trump, the one held by the player who is earliest in rotation wins (playing anticlockwise, the player to dealer's right is first in rotation and the dealer is last). A particular card may not be involved in two declarations (except in Belote-Rebelote).
        @source: https://www.pagat.com/jass/belote.html#deal%20and%20bidding
     * 
     * EDIT: 202311: all declarations of a TEAM with highest declaration should be resolved and played
     */
    public ResolvePlayersDeclarations(): PlayerDeclaration[] {
        // console.log("ResolvePlayersDeclarations");

        // get ref
        const stateMachine: TableStateMachine = this.table.GetStateMachine();
        let trumpSuit: TrumpSuit = stateMachine.GetData().trumpSuit;
        let declarations: PlayerDeclaration[] = [];
        const players: Players = this.table.players;
        const playersCount: number = players.GetLength();
        // true if game mode allows it (Belote|Coinche) AND if options toggle is ON
        const playWithDeclarations: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.playWithDeclarations, this.table.variationId);
        const isPlayWithDeclarations: boolean = playWithDeclarations && (playWithDeclarations.value as boolean);

        for (let i: number = 0; i < playersCount; i++) {
            // console.log("> player:",i);
            // declarations[i] = [];
            // get ref
            const player: Player = players.GetPlayerByIndex(i);
            const playerCards: Card[] = player.GetCards();
            // player major declarations
            if (isPlayWithDeclarations) {
                // SQUARE
                const playerDeclarationsSquare: PlayerDeclaration[] = this.GetPlayerDeclarationsSquares(i, playerCards, trumpSuit);
                declarations.push(...playerDeclarationsSquare);
                // SEQUENCE
                const playerDeclarationsSequence: PlayerDeclaration[] = this.GetPlayerDeclarationsSequences(i, playerCards, trumpSuit);
                declarations.push(...playerDeclarationsSequence);
            }
            
            const playerDeclarationsBelote: PlayerDeclaration[] = this.GetPlayerDeclarationsBelote(i, playerCards, trumpSuit);
            declarations.push(...playerDeclarationsBelote);
        }

        // DETERMINE HIGHEST MAJOR DECLARATION, and thus, team that will show its major declarations.
        // if got any declarations
        let highestMajorDeclaration: PlayerDeclaration = null;
        if (declarations.length > 0) {
            // sort declarations by value (mutates)
            declarations.sort((decA, decB) => decB.declarationValue - decA.declarationValue);
            // highest value we know, however some values are shared by same types (eg sequence & square)
            // and same types with diff. cards can also have same value
            // AND WE ONLY NEED 1 BEST MAJOR declaration.
            const highestDeclarationValue: number = declarations[0].declarationValue;
            // get all MAJOR (not BELOTE) declarations of highest value
            const highestMajorDeclarations: PlayerDeclaration[] = declarations.filter(dec => ((dec.declarationValue == highestDeclarationValue) && (dec.declarationType != DeclarationType.BELOTE_REBELOTE)));
            // if got 2 or more competing MAJOR declarations of the same, highest value
            if (highestMajorDeclarations.length > 1) {
                // get ref
                const sequences: PlayerDeclaration[] = declarations.filter(d =>
                    (d.declarationType == DeclarationType.TIERCE) ||
                    (d.declarationType == DeclarationType.QUARTE) ||
                    (d.declarationType == DeclarationType.QUINT)
                );
                const squares: PlayerDeclaration[] = declarations.filter(d =>
                    (d.declarationType == DeclarationType.SQUARE)
                );
                // 1) squares always beat (any) sequences
                if (squares.length > 0) {
                    // use squares to determine highest declaration
                    // if got competing squares
                    if (squares.length > 1) {
                        // sort squares by rank value
                        // return highest rank square as highest declaration
                        highestMajorDeclaration = squares.sort((sq1, sq2) => {
                            return sq2.declarationCards.cards[0].rank - sq1.declarationCards.cards[0].rank
                        })[0];
                    }
                    else {
                        highestMajorDeclaration = squares[0];
                    }
                }
                // 2) got 2+ sequences:
                // 2A) highest length sequence wins; but if they are same length,
                // 2B) highest rank sequence wins; but if they are ALSO same rank,
                // 2C) trump sequence wins; but if NONE is trump, or we play ALLTRUMPS(?) or NOTRUMPS(?),
                // 2D) the one held by the player who is earliest in rotation wins (playDirection matters)
                else {
                    // sort them by length
                    sequences.sort((s1, s2) => {
                        return s2.declarationCards.cards.length - s1.declarationCards.cards.length;
                    });
                    // 2A) only one sequence with highest length
                    if (sequences[0].declarationCards.cards.length != sequences[1].declarationCards.cards.length) {
                        // highest major declaration found
                        highestMajorDeclaration = sequences[0];
                    }
                    // more than 1 sequence with highest length
                    else {
                        const highestSequenceLength: number = sequences[0].declarationCards.cards.length;
                        // filter out only highest-length sequences
                        let reducedSequences: PlayerDeclaration[] = sequences.filter(seq => seq.declarationCards.cards.length == highestSequenceLength);
                        // and sort them by rank (highest first)
                        reducedSequences.sort((s1, s2) => {
                            return s2.declarationCards.cards[s2.declarationCards.cards.length - 1].rank - s1.declarationCards.cards[s1.declarationCards.cards.length - 1].rank;
                        });
                        // 2B) only one sequence with highest rank
                        if (reducedSequences[0].declarationCards.cards[reducedSequences[0].declarationCards.cards.length - 1].rank != reducedSequences[1].declarationCards.cards[reducedSequences[1].declarationCards.cards.length - 1].rank) {
                            // highest major declaration found
                            highestMajorDeclaration = reducedSequences[0];
                        }
                        // more than 1 sequence with highest rank
                        else {
                            const highestSequenceRank: number = reducedSequences[0].declarationCards.cards[reducedSequences[0].declarationCards.cards.length - 1].rank;
                            // filter out only highest-rank sequences
                            reducedSequences = reducedSequences.filter(seq => seq.declarationCards.cards[seq.declarationCards.cards.length - 1].rank == highestSequenceRank);

                            // 2C) trump sequence wins
                            // get trump sequences (exception: ALLTRUMPS/NOTRUMPS, in which case seq count remains unchanged)
                            if (trumpSuit != TrumpSuit.ALLTRUMPS && trumpSuit != TrumpSuit.NOTRUMPS) {
                                // check if one of the sequences is not a trump suit
                                // (there can only be one, or none)
                                const redTrumpSequences = reducedSequences.filter(seq => {
                                    return CardSuitToTrumpSuit(seq.declarationCards.cards[0].suit) == trumpSuit;
                                });
                                if (redTrumpSequences.length == 1) {
                                    highestMajorDeclaration = redTrumpSequences[0];
                                }
                            }
                            // no trump sequence or NOTRUMPS/ALLTRUMPS mode
                            if (highestMajorDeclaration == null) {
                                // 2D) the one held by the player who is earliest in rotation wins
                                const playDirection: GamePlayDirection = this.table.GetStateMachine().GetData().options.gamePlayDirection;
                                let nextPlayerIndex: number = stateMachine.GetFixedPlayerIndex(stateMachine.GetData().lastPlayedPlayerIndex + playDirection);
                                for (let j = 0; j < playersCount - 1; j++) {
                                    if (highestMajorDeclaration != null) {
                                        break;
                                    }
                                    const checkPlayerIndex: number = stateMachine.GetFixedPlayerIndex(nextPlayerIndex + j * playDirection);
                                    for (let k = 0; k < reducedSequences.length; k++) {
                                        if (reducedSequences[k].playerIndex == checkPlayerIndex) {
                                            highestMajorDeclaration = reducedSequences[k];
                                            break;
                                        }
                                    }
                                }
                                if (highestMajorDeclaration == null) {
                                    console.warn("ResolvePlayersDeclarations: was not able to exactly ascertain highest major declaration!");
                                    highestMajorDeclaration = reducedSequences[0];
                                }
                            }
                        }
                    }
                }
            }
            else {
                highestMajorDeclaration = highestMajorDeclarations[0];
            }
        }

        // get ref
        const belotes: PlayerDeclaration[] = declarations.filter(d =>
            (d.declarationType == DeclarationType.BELOTE_REBELOTE)
        );
        // console.log("belotes=",belotes);
        // console.log("highestMajorDeclaration",highestMajorDeclaration);

        // only return major declarations from team with highest declaration
        let majDeclsFromHighestTeam: PlayerDeclaration[] = [];
        if (highestMajorDeclaration != null) {
            const playerIndex = highestMajorDeclaration.playerIndex;
            const teammateIndex = (highestMajorDeclaration.playerIndex + 2) % 4;
            majDeclsFromHighestTeam = declarations.filter(d=>
                d.declarationType != DeclarationType.BELOTE_REBELOTE &&
                (d.playerIndex == playerIndex || d.playerIndex == teammateIndex)
            );
            // console.log("our team's major declarations",majDeclFromOurTeam);
        }
        
        return majDeclsFromHighestTeam.concat(belotes);

        // console.log("Tracing reduced declarations ("+declarations.length+") ..");
        // declarations.forEach((declaration)=>{
        //     console.log("Player"+declaration.playerIndex+"'s "+DeclarationType[declaration.declarationType]+" declaration:", declaration.declarationCards.cards.map(card=>`${CardSuit[card.suit]} ${CardRank[card.rank]}`),"value:",declaration.declarationValue);
        // });

        // console.log("declarations winner:");
        // declarations.forEach((declaration, index)=>{
        //     console.log("Player"+declaration.playerIndex+"'s declaration:", declaration.declarationCards.cards.map(card=>`${CardSuit[card.suit]} ${CardRank[card.rank]}`),"value:",declaration.declarationValue);
        // });

        // return declarations;
    }

    /**
     * Resolve player cards and returns all SQUARE declarations on hand. Raw, unsorted.
     * NOTE: In super-rare cases (BELOTE, COINCHE, CONTREE), can have more than one SQUARE on hand
     */
    private GetPlayerDeclarationsSquares(
        playerIndex: number,
        playerCards: Card[],
        trumpSuit: TrumpSuit
    ): PlayerDeclaration[] {
        const declarations: PlayerDeclaration[] = [];
        const rankCounts: number[] = [];
        playerCards.forEach(card => {
            if (rankCounts[card.rank] == undefined) {
                rankCounts[card.rank] = 0;
            }
            rankCounts[card.rank]++;
        });
        // console.log(rankCounts.map((rankCount,cardRank)=>CardRank[cardRank]+"="+rankCount))

        // now see if there are 4 of any CardRank (this can happen twice)
        // except SEVENS and EIGHTS, which dont form a SQUARE
        rankCounts.forEach((rankCount, cardRank) => {
            // if found SQUARE
            if (rankCount == 4 && cardRank != CardRank.SEVEN && cardRank != CardRank.EIGHT) {
                // create a new declaration for it
                const declaration: PlayerDeclaration = {
                    playerIndex: playerIndex,
                    declarationType: DeclarationType.SQUARE,
                    declarationValue: this.GetDeclarationValue(DeclarationType.SQUARE, cardRank, trumpSuit),
                    declarationCards: this.CreateDeclarationCardsSquare(cardRank),
                }
                // and push it into all other player declarations
                declarations.push(declaration);
            }
        });
        return declarations;
    }

    private CreateDeclarationCardsSquare(cardRank: CardRank): DeclarationCards {
        return {
            cards: [
                {
                    suit: CardSuit.SPADES,
                    rank: cardRank
                },
                {
                    suit: CardSuit.CLUBS,
                    rank: cardRank
                },
                {
                    suit: CardSuit.DIAMONDS,
                    rank: cardRank
                },
                {
                    suit: CardSuit.HEARTS,
                    rank: cardRank
                },
            ],
            played: [
                false, false, false, false
            ]
        };
    }

    private GetBestSquare(squares: PlayerDeclaration[]): PlayerDeclaration {
        // in case of only one square array, return that square
        if (squares.length == 1) {
            return squares[0];
        }

        // sort in place, value-descending (first is highest value)
        squares.sort((squareA, squareB) => {
            return squareB.declarationValue - squareA.declarationValue
        });

        // TODO: maybe: if there are items of the SAME value, do additional sorting.. by something (dealerIndex?)
        // ...

        // return highest value square
        return squares[0];
    }

    /**
     * Returns all declaration sequences a player has on hand.
     * Declaration sequence can be declared for ANY suit.
     */
    private GetPlayerDeclarationsSequences(
        playerIndex: number,
        playerCards: Card[],
        trumpSuit: TrumpSuit,
    ): PlayerDeclaration[] {
        // SEQUENCE (QUINT > QUARTE > TIERCE) (return highest value declaration)
        const declarations: PlayerDeclaration[] = [];
        // SEQUENCE (QUINT > QUARTE > TIERCE) (return highest value declaration)
        let cardsBySuit: CardData[][] = [];
        for (let j: number = 0; j < playerCards.length; j++) {
            const card: Card = playerCards[j];
            if (cardsBySuit[card.suit] == undefined) {
                cardsBySuit[card.suit] = [];
            }
            // cardsBySuit[card.suit].push({rank:card.rank, suit:card.suit});
            cardsBySuit[card.suit].push(card.cardData);
        }

        // console.log(i, cardsBySuit.map(suitCards=>{
        //     return suitCards.map(card=>{
        //         return "{rank:"+CardRank[card.rank]+", suit:"+CardSuit[card.suit]+"}";
        //     })
        // }))

        // 3+ of same suit only (sequence needs at least 3 cards)
        const cardsBySuitReduced = cardsBySuit.filter(suitCards => suitCards.length > 2);

        // console.log(i, cardsBySuitReduced.map(suitCards=>{
        //     return suitCards.map(card=>{
        //         return "{rank:"+CardRank[card.rank]+", suit:"+CardSuit[card.suit]+"}";
        //     })
        // }))

        // sort descending; sorts in place;
        // longer suit 'possibly sequences' first in collection
        cardsBySuitReduced.sort((suitCardsA, suitCardsB) => suitCardsB.length - suitCardsA.length);

        // console.log(i, cardsBySuitReduced.map(suitCards=>{
        //     return suitCards.map(card=>{
        //         return "{rank:"+CardRank[card.rank]+", suit:"+CardSuit[card.suit]+"}";
        //     })
        // }))

        // let playerSequences = [];
        // check for sequence
        for (let i: number = 0; i < cardsBySuitReduced.length; i++) {
            // console.log(">> suit:",i);
            const suitCards: CardData[] = cardsBySuitReduced[i];
            // sort card ranks ascending
            // ascending is good for display, but we will test for sequence from top to bottom (higher is better)
            suitCards.sort((cardA: CardData, cardB: CardData) => { return cardA.rank - cardB.rank });

            let sequenceCardsCount: number = 0;
            let longestSequenceCardsCount: number = 0;
            let longestSequenceCardsStartIndex: number = 0; // from top

            let startIndex: number;
            // 3 is minimum length of SEQUENCE declaration (TIERCE)
            for (let j = 0; j < suitCards.length - 2; j++) { // 3 cards = 1 cycle (1 TIERCE sequence check)
                // console.log(">>> suitCycle:",j)
                startIndex = suitCards.length - 1 - j; // 4 cards, start at index 3
                sequenceCardsCount = 1; // needs at least 2 cards to be a sequence
                let suitCard: CardData = suitCards[startIndex];
                for (let k = startIndex - 1; k >= 0; k--) { // compare with index 2, then index 2 to index 1 down to 0
                    // console.log(">>>> card:", (k+1)+"->"+k);
                    // console.log(CardRank[suitCard.rank]+"-"+CardSuit[suitCard.suit],"down to",CardRank[suitCards[k].rank]+"-"+CardSuit[suitCards[k].suit]);
                    // as long as cards are in descending sequence
                    if (suitCards[k].rank == suitCard.rank - 1) {
                        // console.log("sequence chain");
                        // keep increasing sequence count
                        sequenceCardsCount++;
                        // if current sequence exceeds length of so far found sequence
                        if (sequenceCardsCount > longestSequenceCardsCount) {
                            // save it 
                            longestSequenceCardsCount = sequenceCardsCount;
                            longestSequenceCardsStartIndex = startIndex;
                            // console.log("record found! longestSequenceCardsCount=",longestSequenceCardsCount,"longestSequenceCardsStartIndex=",longestSequenceCardsStartIndex);
                        }
                    }
                    // if next 2 cards are not in descending sequence
                    else {
                        // console.log("sequence broken");
                        // reset length counter
                        sequenceCardsCount = 0;
                        // and break to next cycle
                        break;
                    }
                    suitCard = suitCards[k];
                }
            }

            // after searching for longest sequence from suitCards
            if (longestSequenceCardsCount > 2) {
                // console.log(playerIndex, suitCards.map(card=>`${CardRank[card.rank]} ${CardSuit[card.suit]}`));
                // console.log("longest usable sequence ("+longestSequenceCardsCount+") for suit "+CardSuit[suitCards[0].suit]+" starts at index "+longestSequenceCardsStartIndex);

                // playerSequences.push({
                //     playerIndex: playerIndex,
                //     length: longestSequenceCardsCount,
                //     // EDIT: startIndex is useless outside of 'within suitCards array'
                //     // startIndex: longestSequenceCardsStartIndex - longestSequenceCardsCount + 1,
                //     highestCardRank: suitCards[longestSequenceCardsStartIndex].rank,
                //     cards: suitCards.slice(longestSequenceCardsStartIndex - longestSequenceCardsCount + 1, longestSequenceCardsStartIndex + 1),
                // });

                const cardRank: CardRank = suitCards[0].rank
                const declarationType: DeclarationType = this.GetDeclarationTypeFromSequenceLength(longestSequenceCardsCount);
                const declarationCards: CardData[] = suitCards.slice(longestSequenceCardsStartIndex - longestSequenceCardsCount + 1, longestSequenceCardsStartIndex + 1);
                declarations.push({
                    playerIndex: playerIndex,
                    declarationType: declarationType,
                    declarationValue: this.GetDeclarationValue(declarationType, cardRank, trumpSuit),
                    declarationCards: {
                        cards: declarationCards,
                        played: declarationCards.map(card => false), // as many 'false' items as there are cards
                    }
                });
            }

            // console.log(
            //     i, // cardSuit
            //     CardSuit[suitCards[0].suit], // current suit
            //     suitCards.map(card=>`${CardRank[card.rank]} ${CardSuit[card.suit]}`),
            //     longestSequenceCardsCount, // longest sequence for current suit
            //     longestSequenceCardsStartIndex // sequence start index (from top)
            // );
        }

        return declarations;
    }

    private GetBestSequence(sequences: PlayerDeclaration[]): PlayerDeclaration {
        let reducedSequences: PlayerDeclaration[] = sequences.slice();
        // if there are multiple sequences
        // NOTE: this array will get max length of 2 (8 cards per hand, minimum 3 cards per sequence..)
        // however we will generalize it for future (in case min length of seq or card count per hand changes)
        if (sequences.length > 1) {
            // sort playerSequences descending, by length
            sequences.sort((seqA, seqB) => seqB.declarationCards.cards.length - seqA.declarationCards.cards.length);
            // if there are sequences of different length
            if (sequences[0].declarationCards.cards.length !=
                sequences[sequences.length - 1].declarationCards.cards.length) {
                // only get sequences of highest length
                reducedSequences = sequences.filter(seq => seq.declarationCards.cards.length == sequences[0].declarationCards.cards.length);
            }

            // if there are (still) sequences of the same highest length
            if (reducedSequences.length > 1) {
                // sort them by highest ranked card (since the items are already sorted card-rank ascending, just check the top-most card)
                reducedSequences = reducedSequences.sort((seqA, seqB) => this.GetHighestSequenceCardRank(seqB) - this.GetHighestSequenceCardRank(seqA));
            }
        }

        return reducedSequences[0];
    }

    /**
     * Ranks in sequences (Ascending): 7,8,9,10,J,Q,K,A
     * @param sequence 
     * @returns 
     */
    private GetHighestSequenceCardRank(sequence: PlayerDeclaration): number {
        const cards: CardData[] = sequence.declarationCards.cards;
        let highestCardRank: number = 0;
        for (let i: number = 0; i < cards.length; i++) {
            const card: CardData = cards[i];
            if (card.rank > highestCardRank) {
                highestCardRank = card.rank;
            }
        }
        return highestCardRank;
    }

    private GetDeclarationTypeFromSequenceLength(seqLength: number): DeclarationType {
        switch (seqLength) {
            case 3: return DeclarationType.TIERCE;
            case 4: return DeclarationType.QUARTE;
            case 5: return DeclarationType.QUINT;
            default:
                console.log("GetDeclarationTypeFromSequenceLength: Unrecognized sequence length: ", seqLength);
        }
        // @ts-ignore
        return -1;
    }

    /**
     * Get all belotes on hand
     * - belote cards are K+J, matching trump suit
     * - in case of @see TrumpSuit.ALL_TRUMPS AND Options toggle beloteRebeloteAtAllTrumps=TRUE, there might be up to 4 BELOTEs found on players hands (max 4 among all players; in special case 4 on a single player only)
     */
    private GetPlayerDeclarationsBelote(
        playerIndex: number,
        playerCards: Card[],
        trumpSuit: TrumpSuit,
    ): PlayerDeclaration[] {
        const declarations: PlayerDeclaration[] = [];

        let cardsBySuit: CardData[][] = [];
        let cardSuit: CardSuit;
        let cardSuitAsTrumpSuit: TrumpSuit;
        // ALLTRUMPS 4 belote/rebelote possible, but only if beloteRebeloteAtAllTrumps=TRUE
        const beloteRebeloteAtAllTrumps: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.beloteRebeloteAtAllTrumps, this.table.variationId);
        for (let i: number = 0; i < playerCards.length; i++) {
            const card: Card = playerCards[i];
            cardSuit = card.suit;
            cardSuitAsTrumpSuit = CardSuitToTrumpSuit(cardSuit);
            if (cardSuitAsTrumpSuit == trumpSuit || ((trumpSuit == TrumpSuit.ALLTRUMPS) && beloteRebeloteAtAllTrumps && beloteRebeloteAtAllTrumps.value)) {
                if (cardsBySuit[cardSuit] == undefined) {
                    cardsBySuit[cardSuit] = [];
                }
                cardsBySuit[cardSuit].push({ rank: card.rank, suit: cardSuit });
            }
        }

        for (let i: number = 0; i < cardsBySuit.length; i++) {
            const suitCards: CardData[] = cardsBySuit[i];
            // console.log(i, suitCards);
            if (suitCards != undefined) {
                let hasKing: boolean = false;
                let hasQueen: boolean = false;
                for (let j: number = 0; j < suitCards.length; j++) {
                    const card: CardData = suitCards[j];
                    const cardRank: number = card.rank;
                    if (cardRank == CardRank.KING) {
                        hasKing = true;
                    }
                    else
                        if (cardRank == CardRank.QUEEN) {
                            hasQueen = true;
                        }
                    if (hasKing && hasQueen) {
                        cardSuit = card.suit;
                        // create and add new PlayerDeclaration
                        const cards: CardData[] = [
                            { rank: CardRank.QUEEN, suit: cardSuit },
                            { rank: CardRank.KING, suit: cardSuit },
                        ];
                        const declarationType: DeclarationType = DeclarationType.BELOTE_REBELOTE;
                        declarations.push({
                            playerIndex: playerIndex,
                            declarationType: declarationType,
                            declarationValue: this.GetDeclarationValue(declarationType),
                            declarationCards: {
                                cards: cards,
                                played: cards.map(card => false),
                            }
                        });
                        // go next cycle / next suit
                        break;
                    }
                }
            }
        }

        return declarations;
    }

    /**
     * Return value of declaration
     * @param type 
     * @param cardRank only required for DeclarationType.SQUARE
     * @param trumpSuit  only required for DeclarationType.SQUARE
     */
    public GetDeclarationValue(
        declarationType: DeclarationType,
        cardRank: CardRank = undefined,
        trumpSuit: TrumpSuit = undefined,
    ): number {
        switch (declarationType) {
            case DeclarationType.BELOTE_REBELOTE:
                return 20;
            case DeclarationType.TIERCE:
                return 20;
            case DeclarationType.QUARTE:
                return 50;
            case DeclarationType.QUINT:
                return 100;
            // NOTE: Squares in NT contract have different values, than other contracts
            case DeclarationType.SQUARE:
                switch (cardRank) {
                    case CardRank.JACK:
                        if (trumpSuit == TrumpSuit.NOTRUMPS) {
                            return 100;
                        }
                        return 200;

                    case CardRank.NINE:
                        if (trumpSuit == TrumpSuit.NOTRUMPS) {
                            return 100;
                        }
                        return 150;

                    case CardRank.ACE:
                        if (trumpSuit == TrumpSuit.NOTRUMPS) {
                            return 200;
                        }
                        return 100;

                    case CardRank.TEN:
                        if (trumpSuit == TrumpSuit.NOTRUMPS) {
                            return 150;
                        }
                        return 100;

                    case CardRank.KING:
                        if (trumpSuit == TrumpSuit.NOTRUMPS) {
                            return 100;
                        }
                        return 100;

                    case CardRank.QUEEN:
                        if (trumpSuit == TrumpSuit.NOTRUMPS) {
                            return 100;
                        }
                        return 100;
                }
        }
        console.warn("DeclarationsManager::GetDeclarationValue; Unrecognized declaration:", DeclarationType[declarationType], CardRank[cardRank], TrumpSuit[trumpSuit]);
        return 0;
    }

    /**
     * Kill tweens/timers (for now)
     */
    public Reset() {
        this.waitTween?.destroy();
    }
}